import { useEffect, useState, useCallback } from "react";
import { formatDateTime } from "../lib/dateFormat";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { api } from "../lib/api";
import { Play, RefreshCw, AlertCircle, CheckCircle2, Clock, Loader2, X } from "lucide-react";

function StatusPill({ status }) {
  const palette = {
    success:  { bg: "#dcfce7", fg: "#14532d", icon: CheckCircle2 },
    partial:  { bg: "#fef3c7", fg: "#854d0e", icon: CheckCircle2 },
    running:  { bg: "#dbeafe", fg: "#1e3a8a", icon: Loader2 },
    pending:  { bg: "#e0e7ff", fg: "#3730a3", icon: Clock },
    retrying: { bg: "#fde68a", fg: "#854d0e", icon: RefreshCw },
    failed:   { bg: "#fee2e2", fg: "#7f1d1d", icon: AlertCircle },
  }[status] || { bg: "#e5e7eb", fg: "#374151", icon: Clock };
  const Icon = palette.icon;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded text-[10.5px] font-mono uppercase tracking-wide" style={{ background: palette.bg, color: palette.fg }}>
      <Icon size={10} className={status === "running" ? "animate-spin" : ""} />
      {status || "—"}
    </span>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try { return formatDateTime(iso); } catch (_) { return iso; }
}

export default function EtlAdmin() {
  const [health, setHealth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({}); // jobId -> bool
  const [openJob, setOpenJob] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [h, j] = await Promise.all([
        api.get("/etl/health"),
        api.get("/etl/jobs"),
      ]);
      setHealth(h.data);
      setJobs(j.data.jobs || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (jobId) => {
    setBusy((s) => ({ ...s, [jobId]: true }));
    try {
      await api.post(`/etl/jobs/${jobId}/run`);
      await load();
    } finally {
      setBusy((s) => ({ ...s, [jobId]: false }));
    }
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 min-w-0">
        <Topbar title="ETL Admin" subtitle="Warehouse ETL jobs, runs, watermarks and logs." live onRefresh={load} refreshing={loading} />
        <div className="px-6 py-5 space-y-4">

          {/* Warehouse status banner */}
          <div className="card-flat p-3" data-testid="etl-health">
            {health?.configured ? (
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded text-[10.5px] uppercase font-mono" style={{ background: "#dcfce7", color: "#14532d" }}>
                  <CheckCircle2 size={11} /> Warehouse online
                </span>
                <span style={{ color: "var(--c-text-muted)" }}>ETL_DB_URL is configured and reachable.</span>
              </div>
            ) : (
              <div className="text-[12.5px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded text-[10.5px] uppercase font-mono" style={{ background: "#fee2e2", color: "#7f1d1d" }}>
                    <AlertCircle size={11} /> Warehouse not configured
                  </span>
                  {health?.detail && <span style={{ color: "var(--c-text-muted)" }}>{health.detail}</span>}
                </div>
                <div style={{ color: "var(--c-text-muted)" }}>
                  Ask the DBA to run <code className="font-mono">/app/docs/etl_dba_setup.sql</code>, then add
                  <code className="font-mono mx-1">ETL_DB_URL=mysql+pymysql://etl_user:&lt;pwd&gt;@&lt;host&gt;:3306/camaroo_analytics_wh?charset=utf8mb4</code>
                  to <code className="font-mono">/app/backend/.env</code> and restart. Jobs are listed below but stay disabled until then.
                </div>
              </div>
            )}
          </div>

          {err && (
            <div className="card-flat p-3 text-[12.5px]" style={{ color: "#7f1d1d" }}>
              <AlertCircle size={12} className="inline mr-1" />{err}
            </div>
          )}

          {/* Jobs table */}
          <div className="card-flat" data-testid="etl-jobs-table">
            <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "var(--c-border)" }}>
              <div className="text-[12.5px] font-medium">Jobs ({jobs.length})</div>
              <button className="btn btn-outline" onClick={load} data-testid="etl-refresh"><RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh</button>
            </div>
            <table className="tbl text-[12.5px]">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Target table</th>
                  <th>Mode</th>
                  <th>Schedule</th>
                  <th>Status</th>
                  <th>Last success</th>
                  <th>Rows (ext / load / fail)</th>
                  <th>Watermark</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const lr = j.latest_run || {};
                  return (
                    <tr key={j.id} data-testid={`etl-job-${j.id}`}>
                      <td>
                        <div className="font-medium">{j.name}</div>
                        <div className="text-[10.5px] font-mono" style={{ color: "var(--c-text-muted)" }}>{j.id}</div>
                      </td>
                      <td className="font-mono">{j.target_table}</td>
                      <td><span className="pill" style={{ background: "#eef2ff", color: "#3730a3" }}>{j.mode}</span></td>
                      <td className="font-mono">{j.schedule_cron || "manual"}</td>
                      <td><StatusPill status={lr.status} /></td>
                      <td>{fmtTs(j.last_success_at)}</td>
                      <td className="numeric">
                        {lr.rows_extracted != null ? `${lr.rows_extracted.toLocaleString()} / ${lr.rows_loaded.toLocaleString()} / ${lr.failed_rows.toLocaleString()}` : "—"}
                      </td>
                      <td className="numeric">{j.watermark != null ? j.watermark.toLocaleString() : "—"}</td>
                      <td className="text-right whitespace-nowrap">
                        <button className="btn btn-ghost text-[11px]" onClick={() => setOpenJob(j)} data-testid={`etl-history-${j.id}`}>History</button>
                        <button
                          className="btn btn-outline text-[11px] ml-1"
                          onClick={() => runNow(j.id)}
                          disabled={busy[j.id] || !health?.configured}
                          title={!health?.configured ? "Warehouse not configured" : "Run this job now"}
                          data-testid={`etl-run-${j.id}`}
                        >
                          {busy[j.id] ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Run
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && jobs.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>No ETL jobs registered.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {openJob && <JobHistory job={openJob} onClose={() => setOpenJob(null)} />}
    </div>
  );
}

function JobHistory({ job, onClose }) {
  const [runs, setRuns] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [retryBusy, setRetryBusy] = useState({});

  const load = useCallback(() => {
    api.get(`/etl/jobs/${job.id}/runs`).then((r) => setRuns(r.data.runs || []));
  }, [job.id]);

  useEffect(load, [load]);

  const retry = async (run) => {
    setRetryBusy((s) => ({ ...s, [run.id]: true }));
    try {
      await api.post(`/etl/runs/${run.id}/retry`);
      load();
    } finally {
      setRetryBusy((s) => ({ ...s, [run.id]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end justify-center" onClick={onClose} data-testid="etl-history-drawer">
      <div className="bg-white w-[90vw] max-w-[1100px] h-[70vh] rounded-t-lg shadow-lg border flex flex-col" style={{ borderColor: "var(--c-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--c-border)" }}>
          <div>
            <div className="text-[13px] font-medium">{job.name} — Run history</div>
            <div className="text-[10.5px] font-mono" style={{ color: "var(--c-text-muted)" }}>{job.id}</div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} data-testid="etl-history-close"><X size={14} /></button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="tbl text-[12.5px]">
            <thead><tr>
              <th>Started</th><th>Finished</th><th>Status</th>
              <th>Ext / Load / Fail</th><th>Watermark Δ</th><th>Source</th><th></th>
            </tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{fmtTs(r.started_at)}</td>
                  <td>{fmtTs(r.finished_at)}</td>
                  <td><StatusPill status={r.status} /></td>
                  <td className="numeric">{(r.rows_extracted||0).toLocaleString()} / {(r.rows_loaded||0).toLocaleString()} / {(r.failed_rows||0).toLocaleString()}</td>
                  <td className="numeric">{r.watermark_before ?? 0} → {r.watermark_after ?? "—"}</td>
                  <td>{r.manual ? "manual" : "scheduled"}{r.retry_of ? <div className="text-[10px]" style={{ color: "var(--c-text-muted)" }}>retry of {r.retry_of.slice(0,8)}…</div> : null}</td>
                  <td className="text-right whitespace-nowrap">
                    <button className="btn btn-ghost text-[11px]" onClick={() => setOpenRun(r)} data-testid={`etl-audit-${r.id}`}>Logs</button>
                    {(r.status === "failed" || r.status === "partial") && (
                      <button className="btn btn-outline text-[11px] ml-1" onClick={() => retry(r)} disabled={retryBusy[r.id]} data-testid={`etl-retry-${r.id}`}>
                        {retryBusy[r.id] ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={7} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>No runs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {openRun && <RunAudit run={openRun} onClose={() => setOpenRun(null)} />}
    </div>
  );
}

function RunAudit({ run, onClose }) {
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    api.get(`/etl/runs/${run.id}/audit`).then((r) => setAudit(r.data.audit || []));
  }, [run.id]);
  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center" onClick={onClose} data-testid="etl-audit-drawer">
      <div className="bg-white w-[820px] max-w-[95vw] h-[60vh] rounded-md shadow-lg border flex flex-col" style={{ borderColor: "var(--c-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--c-border)" }}>
          <div className="text-[13px] font-medium">Run audit · <span className="font-mono text-[11px]" style={{ color: "var(--c-text-muted)" }}>{run.id}</span></div>
          <button className="btn btn-ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="overflow-auto flex-1 p-3 font-mono text-[11.5px] space-y-1">
          {run.error && <div className="p-2 rounded" style={{ background: "#fee2e2", color: "#7f1d1d" }}>{run.error}</div>}
          {audit.map((a) => (
            <div key={a.id} className="flex gap-2">
              <span style={{ color: "var(--c-text-muted)" }}>{fmtTs(a.ts)}</span>
              <span style={{ color: a.level === "error" ? "#7f1d1d" : (a.level === "warn" ? "#854d0e" : "#374151") }}>[{a.level}]</span>
              <span>{a.msg}</span>
            </div>
          ))}
          {audit.length === 0 && <div style={{ color: "var(--c-text-muted)" }}>No audit entries.</div>}
        </div>
      </div>
    </div>
  );
}
